import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { datasetAPI } from '../services/api';

function DatasetsList() {
  const [datasets, setDatasets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [showUploadForm, setShowUploadForm] = useState(false);

  const [uploadForm, setUploadForm] = useState({
    file: null,
    name: '',
    description: '',
    tags: ''
  });

  useEffect(() => {
    fetchDatasets();
  }, []);

  const fetchDatasets = async () => {
    try {
      setLoading(true);
      const response = await datasetAPI.getAll();
      setDatasets(response.data);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setUploadForm(prev => ({
      ...prev,
      file,
      name: prev.name || file?.name.replace('.csv', '') || ''
    }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setUploadForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleUpload = async (e) => {
    e.preventDefault();

    if (!uploadForm.file) {
      alert('Please select a file');
      return;
    }

    if (!uploadForm.name.trim()) {
      alert('Please enter a dataset name');
      return;
    }

    try {
      setUploading(true);
      setError(null);

      await datasetAPI.upload(
        uploadForm.file,
        uploadForm.name.trim(),
        uploadForm.description.trim(),
        uploadForm.tags.trim()
      );

      // Reset form
      setUploadForm({
        file: null,
        name: '',
        description: '',
        tags: ''
      });
      setShowUploadForm(false);

      // Refresh list
      fetchDatasets();
    } catch (err) {
      setError('Upload failed: ' + (err.response?.data?.error || err.message));
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Are you sure you want to delete dataset "${name}"?`)) {
      return;
    }

    try {
      await datasetAPI.delete(id);
      fetchDatasets();
    } catch (err) {
      alert('Failed to delete dataset: ' + err.message);
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'N/A';
    const mb = bytes / (1024 * 1024);
    return mb >= 1 ? `${mb.toFixed(2)} MB` : `${(bytes / 1024).toFixed(2)} KB`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="card">
        <div className="flex-between mb-3">
          <h2>Datasets</h2>
          <div className="flex">
            <button
              onClick={() => setShowUploadForm(!showUploadForm)}
              className="btn btn-primary"
            >
              {showUploadForm ? 'Cancel' : 'Upload Dataset'}
            </button>
            <Link to="/" className="btn btn-secondary">
              Back to Test Cases
            </Link>
          </div>
        </div>

        {error && (
          <div className="alert alert-danger">{error}</div>
        )}

        {showUploadForm && (
          <div className="card" style={{ background: '#f9f9f9', padding: '1.5rem', marginBottom: '1.5rem' }}>
            <h3>Upload New Dataset</h3>
            <form onSubmit={handleUpload}>
              <div className="grid grid-2">
                <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                  <label>CSV File *</label>
                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleFileChange}
                    required
                  />
                  <small>Select a CSV file to upload as a dataset</small>
                </div>

                <div className="form-group">
                  <label>Dataset Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={uploadForm.name}
                    onChange={handleInputChange}
                    placeholder="e.g., Product IDs Dataset"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Tags (comma-separated)</label>
                  <input
                    type="text"
                    name="tags"
                    value={uploadForm.tags}
                    onChange={handleInputChange}
                    placeholder="e.g., production, shopee, products"
                  />
                </div>

                <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                  <label>Description</label>
                  <textarea
                    name="description"
                    value={uploadForm.description}
                    onChange={handleInputChange}
                    rows="3"
                    placeholder="Describe this dataset..."
                  />
                </div>
              </div>

              <div className="flex mt-2">
                <button
                  type="submit"
                  className="btn btn-success"
                  disabled={uploading}
                >
                  {uploading ? 'Uploading...' : 'Upload Dataset'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowUploadForm(false)}
                  className="btn btn-secondary"
                  disabled={uploading}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="alert alert-info">
          <strong>Total Datasets:</strong> {datasets.length}
        </div>

        {datasets.length === 0 ? (
          <p>No datasets found. Upload your first dataset to get started!</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f5f5f5', borderBottom: '2px solid #ddd' }}>
                  <th style={{ padding: '0.75rem', textAlign: 'left' }}>Name</th>
                  <th style={{ padding: '0.75rem', textAlign: 'left' }}>Description</th>
                  <th style={{ padding: '0.75rem', textAlign: 'center' }}>Rows</th>
                  <th style={{ padding: '0.75rem', textAlign: 'center' }}>Columns</th>
                  <th style={{ padding: '0.75rem', textAlign: 'center' }}>Size</th>
                  <th style={{ padding: '0.75rem', textAlign: 'left' }}>Tags</th>
                  <th style={{ padding: '0.75rem', textAlign: 'left' }}>Created</th>
                  <th style={{ padding: '0.75rem', textAlign: 'center' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {datasets.map(dataset => (
                  <tr key={dataset._id} style={{ borderBottom: '1px solid #eee' }}>
                    <td style={{ padding: '0.75rem' }}>
                      <strong>{dataset.name}</strong>
                    </td>
                    <td style={{ padding: '0.75rem' }}>
                      {dataset.description || <em>No description</em>}
                    </td>
                    <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                      {dataset.rowCount.toLocaleString()}
                    </td>
                    <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                      {dataset.columns?.length || 0}
                    </td>
                    <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                      {formatFileSize(dataset.fileSize)}
                    </td>
                    <td style={{ padding: '0.75rem' }}>
                      {dataset.tags?.length > 0 ? (
                        dataset.tags.map(tag => (
                          <span
                            key={tag}
                            className="badge badge-info"
                            style={{ marginRight: '0.25rem' }}
                          >
                            {tag}
                          </span>
                        ))
                      ) : (
                        <em>No tags</em>
                      )}
                    </td>
                    <td style={{ padding: '0.75rem', fontSize: '0.875rem' }}>
                      {formatDate(dataset.createdAt)}
                    </td>
                    <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                      <div className="flex" style={{ justifyContent: 'center', gap: '0.5rem' }}>
                        <Link
                          to={`/datasets/${dataset._id}`}
                          className="btn btn-primary"
                          style={{ fontSize: '0.875rem', padding: '0.5rem 1rem' }}
                        >
                          View
                        </Link>
                        <button
                          onClick={() => handleDelete(dataset._id, dataset.name)}
                          className="btn btn-danger"
                          style={{ fontSize: '0.875rem', padding: '0.5rem 1rem' }}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default DatasetsList;
